// components/ClientSchedulesDrawer/ScheduleCard.jsx
import React from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  keyframes,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  LockClock,
  ReportProblem,
  AccessTime,
  LocationOn,
  Person,
  CalendarToday,
} from "@mui/icons-material";
import { formatDateField, formatTime } from "../../../base/FormatTime";

const blinkEffect = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.3; }
  100% { opacity: 1; }
`;

const parseTime = (val) => {
  if (!val) return null;
  const parts = String(val).split(":");
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const s = parseInt(parts[2] || 0, 10);
  if (isNaN(h) || isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, s, 0);
  return d;
};

const calculateTimeDuration = (start, end) => {
  if (!start || !end) return null;
  const startTime = parseTime(start);
  const endTime = parseTime(end);
  if (!startTime || !endTime) return null;

  let diffMs = endTime.getTime() - startTime.getTime();
  if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours > 0 ? `${hours}h ` : ""}${minutes}m`;
};

const getStatusColor = (status) => {
  switch (status) {
    case "Pending":
      return "warning";
    case "Not Started":
      return "info";
    case "In Progress":
      return "primary";
    case "Partially Completed":
      return "secondary";
    case "Completed":
      return "success";
    case "Missed":
      return "error";
    case "Cancelled":
      return "default";
    case "Late":
      return "error";
    default:
      return "default";
  }
};

const ScheduleCard = ({ job, onView, onEditTime, onDelete, isAdmin }) => {
  const hasSeriousBreach = job.employee_details?.some(
    (emp) => emp.punctuality > 15
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: hasSeriousBreach ? "#FEE4E2" : "#E2E8F0",
        backgroundColor: hasSeriousBreach ? "#FFF1F0" : "#FFFFFF",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: hasSeriousBreach ? "#F04438" : "#6366F1",
          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {hasSeriousBreach && (
            <ReportProblem sx={{ fontSize: 20, color: "#D92D20" }} />
          )}
          <Typography variant="subtitle1" fontWeight={700}>
            {job.visit_name || "Unnamed Visit"}
          </Typography>
          {job?.time_critical && (
            <Tooltip title="Time Critical">
              <LockClock
                sx={{
                  fontSize: 16,
                  color: "#D32F2F",
                  animation: `${blinkEffect} 1s infinite`,
                }}
              />
            </Tooltip>
          )}
          <Chip
            label={job.status}
            size="small"
            color={hasSeriousBreach ? "error" : getStatusColor(job.status)}
            sx={{ fontWeight: 600, fontSize: "0.65rem" }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => onView(job.id)}
              sx={{ color: "#6366f1" }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Time">
            <IconButton
              size="small"
              onClick={() => onEditTime(job.id, job.status)}
              sx={{ color: "#f59e0b" }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => onDelete(job.id)}
                sx={{ color: "#ef4444" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarToday sx={{ fontSize: 16, color: "#94A3B8" }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatDateField(job.build_for)}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOn sx={{ fontSize: 16, color: "#94A3B8" }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Area
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {job.area || "N/A"}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTime sx={{ fontSize: 16, color: "#94A3B8" }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Planned Time
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatTime(job.scheduled_start)} - {formatTime(job.scheduled_end)}
                {job.scheduled_start && job.scheduled_end && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      ml: 1,
                      color: hasSeriousBreach ? "#D92D20" : "primary.main",
                      fontWeight: 700,
                    }}
                  >
                    ({calculateTimeDuration(job.scheduled_start, job.scheduled_end)})
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {job.employee_details?.length > 0 && (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                pt: 1,
                borderTop: "1px solid #F1F5F9",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, width: "100%" }}
              >
                Employee Details:
              </Typography>
              {job.employee_details.map((emp, i) => (
                <Chip
                  key={i}
                  label={`${emp.first_name} (${emp.punctuality !== null ? `${emp.punctuality}m` : "--"})`}
                  size="small"
                  sx={{
                    fontSize: "0.65rem",
                    bgcolor: emp.punctuality > 15 ? "#FEE4E2" : "#ECFDF3",
                    color: emp.punctuality > 15 ? "#B42318" : "#027A48",
                    fontWeight: 600,
                  }}
                />
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default ScheduleCard;